// src/components/map/layers/PropertyMarkersLayer.js
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker, Popup } from 'react-leaflet';
import PropTypes from 'prop-types';
import { FaEye, FaHeart, FaFileAlt, FaEnvelope } from 'react-icons/fa'; // Import icons
import { createPriceIcon, createHoverIcon } from '../../../utils/mapIconUtils';
import { PropertyTypeNumericToStringMap } from '../../../utils/enums';
import { useDevLogging } from '../../../hooks/useDevLogging';
import { getFullImageUrl } from '../../../utils/config';
import './PropertyMarkersLayer.css'; // Import CSS for the popup
// Note: formatCurrencyForDisplay is passed as a prop

/**
 * Renders Markers with price icons and popups for property search results.
 *
 * @param {object} props
 * @param {Array<object>} props.properties - Array of property objects to display.
 * @param {function} props.formatCurrencyForDisplay - Function to format currency values.
 * @param {boolean} props.isMyPropertiesView - Flag to indicate if the view is for the seller's own properties.
 */
const PropertyMarkersLayerComponent = ({ properties, formatCurrencyForDisplay, isMyPropertiesView, onPropertySelect }) => {
  useDevLogging('PropertyMarkersLayer.js', { properties, isMyPropertiesView, onPropertySelect });
  // State to manage the currently hovered marker's ID
  const [hoveredPropertyId, setHoveredPropertyId] = useState(null);
  const { t, i18n } = useTranslation();

  // Memoize the default and hover icons to prevent re-creation on every render
  const defaultIcon = useCallback((price) => createPriceIcon(price), []);
  const hoverIcon = useCallback((price) => createHoverIcon(price), []);

  // Render nothing if the array is empty or not provided
  if (!properties || properties.length === 0) {
    return null;
  }

  const handleMouseOver = (propertyId) => {
    setHoveredPropertyId(propertyId);
  };

  const handleMouseOut = () => {
    setHoveredPropertyId(null);
  };

  return (
    <>
      {properties.map((property) => {
        // --- Validation ---
        if (!property.location?.type === 'Point' || !Array.isArray(property.location.coordinates) || property.location.coordinates.length !== 2) {
          console.warn('Skipping property with invalid location structure:', property);
          return null;
        }

        const lon = property.location.coordinates[0];
        const lat = property.location.coordinates[1];

        if (typeof lat !== 'number' || typeof lon !== 'number' || isNaN(lat) || isNaN(lon)) {
          console.warn('Skipping property with invalid coordinates (NaN or not number):', property);
          return null;
        }

        // --- Refined Unique Key Generation ---
        let propertyKey;
        if (property._id) {
          propertyKey = property._id; // Prefer MongoDB _id
        } else if (property.propertyID) {
          propertyKey = `pid-${property.propertyID}`; // Use propertyID if _id is missing
        } else {
          // Fallback - less ideal, use only if necessary
          propertyKey = `prop-${lat.toFixed(6)}-${lon.toFixed(6)}`;
          console.warn(
            `Property missing both _id and propertyID, using fallback key: ${propertyKey}`,
            property // Log the property object for debugging
          );
        }
        // --- End Key Generation ---

        // --- Rendering ---
        return (
          <Marker
            key={propertyKey} // Use the refined key
            position={[lat, lon]} // Leaflet Marker expects [lat, lng]
            icon={hoveredPropertyId === propertyKey ? hoverIcon(property.price) : defaultIcon(property.price)} // Change icon on hover
            eventHandlers={{
              mouseover: () => handleMouseOver(propertyKey),
              mouseout: handleMouseOut,
            }}
          >
            <Popup>
              <div className="property-popup-container" onClick={() => onPropertySelect(property.propertyID)}>
                {property.mainImageURL && (
                  <img
                    src={getFullImageUrl(property.mainImageURL)}
                    alt={property.address || 'Property'}
                    className="popup-property-image"
                    onError={(e) => {
                      e.target.style.display = 'none'; // Hide broken image
                    }}
                  />
                )}
                <div className="popup-details">
                  <div className="popup-price">{formatCurrencyForDisplay(property.price)}</div>
                  <div
                    className="popup-specs"
                    style={{ textAlign: i18n.dir() === 'rtl' ? 'right' : 'left' }}
                    dangerouslySetInnerHTML={{
                      __html: [
                        property.beds ? t('mapSidebar.beds', { count: property.beds }) : null,
                        property.baths ? t('mapSidebar.baths', { count: property.baths }) : null,
                        property.size ? t('mapSidebar.sqft', { size: property.size.toLocaleString() }) : null,
                      ]
                        .filter(Boolean)
                        .join(` ${t('mapSidebar.separator')} `),
                    }}
                  />
                  <div className="popup-address" style={{ direction: 'ltr' }}>
                    {[property.address, property.city, property.state].filter(Boolean).join(', ') || 'Address not available'}
                  </div>
                  {isMyPropertiesView && property.stats && (
                    <div className="popup-stats">
                      <span className="stat-item" title="Views">
                        <FaEye /> {property.stats.viewCount || 0}
                      </span>
                      <span className="stat-item" title="Saves">
                        <FaHeart /> {property.stats.saveCount || 0}
                      </span>
                      <span className="stat-item" title="Reports">
                        <FaFileAlt /> {property.stats.reportCount || 0}
                      </span>
                      <span className="stat-item" title="Inquiries">
                        <FaEnvelope /> {property.stats.inquiryCount || 0}
                      </span>
                    </div>
                  )}
                  <div className="popup-type">{PropertyTypeNumericToStringMap[property.type] || property.type || 'N/A'}</div>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

// Define PropTypes for better component usage understanding and validation
PropertyMarkersLayerComponent.propTypes = {
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      propertyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      _id: PropTypes.string,
      location: PropTypes.shape({
        type: PropTypes.oneOf(['Point']),
        coordinates: PropTypes.arrayOf(PropTypes.number),
      }),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      address: PropTypes.string,
      type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      beds: PropTypes.number,
      baths: PropTypes.number,
      size: PropTypes.number,
      yearBuilt: PropTypes.number,
      stats: PropTypes.shape({
        viewCount: PropTypes.number,
        saveCount: PropTypes.number,
        reportCount: PropTypes.number,
        inquiryCount: PropTypes.number,
      }),
    })
  ),
  formatCurrencyForDisplay: PropTypes.func.isRequired,
  isMyPropertiesView: PropTypes.bool,
  onPropertySelect: PropTypes.func.isRequired,
};

// Set default props for the base component
PropertyMarkersLayerComponent.defaultProps = {
  isMyPropertiesView: false,
};

// Memoize the component after defining its propTypes and defaultProps
const PropertyMarkersLayer = React.memo(PropertyMarkersLayerComponent);

export default PropertyMarkersLayer;
